import React, { useMemo, useState } from 'react';

import { Paragraph } from '@src/stories/elements/Paragraph/Paragraph';
import { Icon } from '@src/stories/elements/Icon/Icon';

import useCustomerWishlist from '@src/hooks/useCustomerWishlist';
import useLocalWishlistStore, { localWishlistStore } from '@src/stores/localWishlistStore';
import { useResponsiveBreakpoints } from '@src/hooks/useMediaQuery';

const WishlistButton = ({ type, settings, product, iconSize, blockSettings }) => {
  const { block_settings, parent_block_settings } = settings;
  const { isMobile } = useResponsiveBreakpoints();

  const {
    padding_top_desktop,
    padding_bottom_desktop,
    padding_left_desktop,
    padding_right_desktop,
    padding_top_mobile,
    padding_bottom_mobile,
    padding_left_mobile,
    padding_right_mobile,
    margin_top_desktop,
    margin_bottom_desktop,
    margin_left_desktop,
    margin_right_desktop,
    margin_top_mobile,
    margin_bottom_mobile,
    margin_left_mobile,
    margin_right_mobile
  } = blockSettings || {};

  const size = iconSize
    ? iconSize
    : isMobile && block_settings?.override_icon_size_mobile
    ? block_settings?.icon_size_mobile
    : block_settings?.icon_size || 24;

  const getShopifyCustomerId = (): string | number | null => {
    return (
      (window as any).getShopifyCustomerId?.() ||
      (window as any).ShopifyAnalytics?.meta?.page?.customerId ||
      (window as any).customer?.id ||
      null
    );
  };

  // Store
  const updateLocalList = useLocalWishlistStore((state) => state.updateLocalList);

  // Customer wishlist
  const { updateCustomerList, createCustomerList, fetchCustomerLists, isProductInWishlist } = useCustomerWishlist();

  // Get customizable settings from parent block
  const buttonType = type || 'icon';
  const activeIcon = parent_block_settings?.wishlistIconActive || blockSettings?.wishlistIconActive || 'wishlist-filled';
  const inactiveIcon = parent_block_settings?.wishlistIconInactive || blockSettings?.wishlistIconInactive || 'wishlist';
  const activeText = parent_block_settings?.wishlistStateTextActive || blockSettings?.wishlistStateTextActive || 'Remove from Wishlist';
  const inactiveText = parent_block_settings?.wishlistStateText || blockSettings?.wishlistStateText || 'Add to Wishlist';

  // Get product context for the wishlist action
  const getProductContext = () => {
    if (typeof window !== 'undefined' && (window as any).getProductContext) {
      return (window as any).getProductContext();
    }
    return null;
  };

  // Get current product ID from context
  const getCurrentProductId = () => {
    if (product) {
      return product.id || null;
    }
    const productContext = getProductContext();
    return productContext?.product?.id || null;
  };

  const isProductInCurrentWishlist = useMemo(() => {
    const currentProductId = getCurrentProductId();
    if (!currentProductId) return false;
    return isProductInWishlist(currentProductId);
  }, [getCurrentProductId(), isProductInWishlist]);

  // Determine current display values based on wishlist state
  const currentText = isProductInCurrentWishlist ? activeText : inactiveText;
  const currentIcon = isProductInCurrentWishlist ? activeIcon : inactiveIcon;
  const isText = buttonType === 'text';

  const [isDisabled, setIsDisabled] = useState(false);

  // Handles Click Event
  const handleClick = async () => {
    setIsDisabled(true);
    const currentProductId = getCurrentProductId();
    const customerId = getShopifyCustomerId();

    if (!currentProductId) {
      console.warn('No product ID available for wishlist action');
      setIsDisabled(false);
      return;
    }

  const localLists = localWishlistStore.getState().localLists;
    const currentList = localLists.find((list) => list.listType === 'products');
    let newProducts: string[] = [];

    if (isProductInCurrentWishlist) {
      newProducts = currentList?.products.filter((productId) => !productId.includes(currentProductId.toString())) || [];
    } else {
      newProducts = [...(currentList?.products || []), `gid://shopify/Product/${currentProductId}`];
    }

    const listName = 'Products';
    const listType = 'products';

    try {
      // If it's logged in customer, update metaobject & metafield
      if (customerId) {
        // Immediately get the returned wishlist metaobject data
        // to use the metaobject ID to update customer's metafield
        const updateListRes = await updateCustomerList({
          handle: `${customerId}-${listType}`,
          listName,
          products: newProducts
        });

        // Add the metaobject to customer's metafield
        // if the currentList doesn't exist
        if (!currentList) {
          const fetchCustomerListsRes = await fetchCustomerLists({ customerId });
          const newListIds = [...(fetchCustomerListsRes?.map((wishlist) => wishlist.id) || []), updateListRes.id];

          createCustomerList({ customerId, wishlistLists: newListIds });
        }
      }

      // Store in local storage
      updateLocalList({ listName, listType, products: newProducts });
    } catch (error) {
      console.error('Failed to toggle wishlist:', error);
    } finally {
      // Remove disabled state after customer list update (or local update if guest)
      setIsDisabled(false);
    }
  };

  return (
    <div className={`${parent_block_settings?.classes || ''}`}>
      {isText ? (
        <div
          onClick={handleClick}
          style={{ cursor: 'pointer' }}
        >
          <Paragraph text={currentText} />
        </div>
      ) : (
        <Icon
          settings={{}}
          iconSource={'icon_name'}
          iconName={currentIcon}
          customIcon={null}
          iconSize={size}
          iconLink={null}
          onClick={handleClick}
          isOverlay={false}
          alignment="center"
          className={`flex items-center h-full ${isDisabled ? 'disabled cursor-wait' : ''}`}
          zIndex={1}
          style={{
            paddingTop: isMobile ? padding_top_mobile : padding_top_desktop,
            paddingBottom: isMobile ? padding_bottom_mobile : padding_bottom_desktop,
            paddingLeft: isMobile ? padding_left_mobile : padding_left_desktop,
            paddingRight: isMobile ? padding_right_mobile : padding_right_desktop,
            marginTop: isMobile ? margin_top_mobile : margin_top_desktop,
            marginBottom: isMobile ? margin_bottom_mobile : margin_bottom_desktop,
            marginLeft: isMobile ? margin_left_mobile : margin_left_desktop,
            marginRight: isMobile ? margin_right_mobile : margin_right_desktop
          }}
        />
      )}
    </div>
  );
};

export default WishlistButton;
